import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { Package, Plus, Search, Grid3x3, List, Trash2, Edit, RefreshCw, AlertTriangle, ImageOff, Crown, SlidersHorizontal, X, Sparkles, Loader2 } from 'lucide-react';
import { useCategories } from '../context/CategoryContext';
import CategorySelect from '../components/CategorySelect';
import Navigation from '../components/Navigation';
import MoreMenu from '../components/MoreMenu';
import { resolveImageUrl } from '../utils/imageUrl';
import WorkflowSelector from '../components/stash/WorkflowSelector';
import AddItemModal from '../components/AddItemModal';
import AIAssistedFlow from '../components/stash/AIAssistedFlow';
import URLLookupFlow from '../components/stash/URLLookupFlow';
import ItemDetailModal from '../components/ItemDetailModal';
import EditItemModal from '../components/EditItemModal';
import { BACKEND_URL } from '../config/api';
import ConfirmDialog from '../components/ConfirmDialog';
import ConsumptionBar from '../components/ConsumptionBar';
import FloatingActionButton from '../components/FloatingActionButton';
import { useIsMobile } from '../hooks/useIsMobile';
import BestMatches, { ScoreBadge } from '../components/search/BestMatches';

// Grid Item Component
const StashGridItem = ({ 
  item, isBroken, imageCacheBuster, isSearchMode,
  onImageError, onFixImage, onAddImage, onItemClick, onEditItem, onDeleteItem 
}) => {
  return (
    <div
      onClick={onItemClick}
      className="bg-white rounded-xl overflow-hidden border border-beige shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-sage transition-all duration-300 group cursor-pointer"
      data-testid={`stash-item-${item.item_id}`}
    >
      <div className="aspect-square bg-beige flex items-center justify-center relative overflow-hidden">
        {item.image_url && !isBroken ? (
          <img 
            src={`${resolveImageUrl(item.image_url)}?v=${imageCacheBuster}`} 
            alt={item.name} 
            className="w-full h-full object-cover"
            onError={() => onImageError(item.item_id)}
          />
        ) : item.image_url && isBroken ? (
          <div className="flex flex-col items-center justify-center p-4 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-2">
              <ImageOff size={24} className="text-amber-600" />
            </div>
            <p className="text-xs text-gray-soft mb-2">Image unavailable</p>
            <button
              onClick={(e) => onFixImage(item, e)}
              className="px-3 py-1.5 bg-sage text-white text-xs rounded-lg hover:bg-sage-dark transition-all"
              data-testid={`fix-image-${item.item_id}`}
            >
              Fix Image
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-4 text-center">
            <div className="w-12 h-12 rounded-full bg-beige flex items-center justify-center mb-2">
              <ImageOff size={24} className="text-gray-light" />
            </div>
            <p className="text-xs text-gray-soft mb-2">No image</p>
            <button
              onClick={(e) => onAddImage(item, e)}
              className="px-3 py-1.5 bg-sage text-white text-xs rounded-lg hover:bg-sage-dark transition-all"
              data-testid={`add-image-${item.item_id}`}
            >
              Add Image
            </button>
          </div>
        )}
        
        {/* Score badge - positioned in image area */}
        {isSearchMode && item.relevance_score !== undefined && (
          <div className="absolute top-2 right-2">
            <ScoreBadge
              badgeId={`grid-${item.item_id}`}
              score={item.relevance_score}
              breakdown={item.score_breakdown}
            />
          </div>
        )}
      </div>

      {item.colors?.length > 0 && (
        <div className="flex gap-1.5 px-4 py-3 border-b border-beige overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {item.colors.map((color, idx) => (
            <div key={idx} className="w-5 h-5 rounded-full border-2 border-white shadow-sm flex-shrink-0" style={{ backgroundColor: color }} title={color} />
          ))}
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between">
          <h3 className="text-base font-medium text-charcoal truncate mb-1 flex-1 min-w-0">{item.name}</h3>
          <div className="ml-2 shrink-0">
            <MoreMenu
              size={16}
              actions={[
                { label: 'Edit', icon: <Edit size={15} />, onClick: () => onEditItem(item), testId: `edit-item-${item.item_id}` },
                { label: 'Delete', icon: <Trash2 size={15} />, onClick: () => onDeleteItem(item), danger: true, testId: `delete-item-${item.item_id}` },
              ]}
            />
          </div>
        </div>
        {item.brand && <p className="text-sm text-gray-soft mb-3">{item.brand}</p>}
        <span className="inline-block px-2.5 py-1 bg-cream text-charcoal text-xs rounded-full">
          {item.category?.replace('_', ' ') || 'Uncategorized'}
        </span>
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="text-xs px-2 py-0.5 bg-beige text-gray-soft rounded-full">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// List Item Component
const StashListItem = ({ 
  item, isBroken, imageCacheBuster, isSearchMode, isLast,
  onImageError, onFixImage, onAddImage, onItemClick, onEditItem, onDeleteItem 
}) => {
  return (
    <div
      onClick={onItemClick}
      className={`p-4 sm:p-6 hover:bg-cream transition-colors group cursor-pointer ${
        !isLast ? 'border-b border-beige' : ''
      }`}
      data-testid={`stash-list-item-${item.item_id}`}
    >
      <div className="flex items-center gap-3 sm:gap-4">
        {/* Thumbnail */}
        {item.image_url && isBroken ? (
          <button
            onClick={(e) => onFixImage(item, e)}
            className="w-14 h-14 sm:w-16 sm:h-16 bg-amber-50 border border-amber-200 rounded-lg flex flex-col items-center justify-center flex-shrink-0 hover:bg-amber-100 transition-colors"
            data-testid={`fix-image-list-${item.item_id}`}
          >
            <ImageOff size={18} className="text-amber-600 mb-0.5" />
            <span className="text-[10px] text-amber-700 font-medium">Fix</span>
          </button>
        ) : (
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-beige rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden relative">
            {item.image_url ? (
              <img 
                src={`${resolveImageUrl(item.image_url)}?v=${imageCacheBuster}`} 
                alt={item.name} 
                className="w-full h-full object-cover"
                onError={() => onImageError(item.item_id)}
              />
            ) : (
              <button
                onClick={(e) => onAddImage(item, e)}
                className="w-full h-full flex flex-col items-center justify-center hover:bg-sage/10 transition-colors"
                data-testid={`add-image-list-${item.item_id}`}
              >
                <ImageOff size={18} className="text-gray-light mb-0.5" />
                <span className="text-[10px] text-sage font-medium">Add</span>
              </button>
            )}
          </div>
        )}
        
        {/* Item info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-medium text-charcoal truncate">{item.name}</h3>
          {item.brand && <p className="text-xs sm:text-sm text-gray-soft truncate">{item.brand}</p>}
        </div>
        
        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Score badge */}
          {isSearchMode && item.relevance_score !== undefined && (
            <ScoreBadge
              badgeId={`list-${item.item_id}`}
              score={item.relevance_score}
              breakdown={item.score_breakdown}
            />
          )}
          {item.colors?.length > 0 && (
            <div className="hidden sm:flex gap-1 overflow-hidden max-w-[120px]">
              {item.colors.map((color, idx) => (
                <div key={idx} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-white shadow-sm flex-shrink-0" style={{ backgroundColor: color }} title={`Color ${idx + 1}`} />
              ))}
            </div>
          )}
          <span className="hidden sm:inline-block px-2 sm:px-3 py-1 bg-cream text-charcoal text-xs sm:text-sm rounded-full whitespace-nowrap">
            {item.category?.replace('_', ' ') || 'Uncategorized'}
          </span>
          <MoreMenu
            size={16}
            actions={[
              { label: 'Edit', icon: <Edit size={15} />, onClick: () => onEditItem(item), testId: `edit-item-list-${item.item_id}` },
              { label: 'Delete', icon: <Trash2 size={15} />, onClick: () => onDeleteItem(item), danger: true, testId: `delete-item-list-${item.item_id}` },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const StashInventory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user, refreshUser } = useAuth();
  const [stashItems, setStashItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('stash_sort') || 'created_at');
  const [showFilters, setShowFilters] = useState(false);
  
  // Smart search state
  const [smartSearchEnabled, setSmartSearchEnabled] = useState(true);
  const [smartSearching, setSmartSearching] = useState(false);
  const [searchIntent, setSearchIntent] = useState(null);
  const searchDebounceRef = useRef(null);
  
  // Grouped search state
  const [bestMatches, setBestMatches] = useState([]);
  const [allSearchResults, setAllSearchResults] = useState([]);
  const [searchGroupings, setSearchGroupings] = useState({ themes: [], matchTypes: [], categories: [] });
  const [activeFilters, setActiveFilters] = useState({ theme: null, matchType: null, category: null });
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Modal state
  const [showWorkflowSelector, setShowWorkflowSelector] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAIFlow, setShowAIFlow] = useState(false);
  const [showURLFlow, setShowURLFlow] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Track broken images by item_id
  const [brokenImages, setBrokenImages] = useState(new Set());
  // Auto-open change image flow when fixing broken image
  const [autoOpenChangeImage, setAutoOpenChangeImage] = useState(false);
  // Cache buster to force image refresh
  const [imageCacheBuster, setImageCacheBuster] = useState(Date.now());
  
  const handleImageError = (itemId) => {
    setBrokenImages(prev => new Set([...prev, itemId]));
  };
  
  const handleFixImage = (item, e) => {
    if (e) e.stopPropagation();
    if (isMobile) {
      navigate(`/stash/edit/${item.item_id}?changeImage=true`);
    } else {
      setSelectedItem(item);
      setAutoOpenChangeImage(true);
      setIsEditModalOpen(true);
    }
  };

  const handleAddImage = (item, e) => {
    if (e) e.stopPropagation();
    if (isMobile) {
      navigate(`/stash/edit/${item.item_id}?changeImage=true`);
    } else {
      setSelectedItem(item);
      setAutoOpenChangeImage(true);
      setIsEditModalOpen(true);
    }
  };

  // Helper to open edit - navigates on mobile, opens modal on desktop
  const handleEditItem = (item, e) => {
    if (e) e.stopPropagation();
    if (isMobile) {
      navigate(`/stash/edit/${item.item_id}`);
    } else {
      setSelectedItem(item);
      setIsEditModalOpen(true);
    }
  };

  // Check if user is at their stash limit
  const isUnlimited = user?.is_unlimited === true;
  const isAtLimit = !isUnlimited && user && (user.stash_item_count >= user.stash_item_limit);
  const currentCount = user?.stash_item_count || 0;
  const currentLimit = user?.stash_item_limit || 20;

  // Handle refresh when coming back from edit page (mobile)
  useEffect(() => {
    if (location.state?.refresh) {
      fetchStashItems();
      // Clear the state so it doesn't refetch on every render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    // Clear smart search intent when switching to basic search
    if (!smartSearchEnabled || !searchQuery) {
      setSearchIntent(null);
    }
    
    // For basic search or when no query, use regular fetch
    if (!smartSearchEnabled || !searchQuery.trim()) {
      fetchStashItems();
      return;
    }
    
    // For smart search, we only search on Enter press (not on every keystroke)
    // This prevents multiple API calls while typing and is more mobile-friendly
    // The useEffect still needs to run to handle the basic search fallback above
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, selectedCategory, sortBy, smartSearchEnabled]);

  const performSmartSearch = async (query) => {
    if (!query.trim()) {
      clearSearchMode();
      return;
    }
    
    setSmartSearching(true);
    setSearchIntent(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/stash/search/smart`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          query: query,
          include_stashed_away: false,
          limit: 100
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Set grouped search results
        setBestMatches(data.best_matches || []);
        setAllSearchResults(data.results || []);
        setSearchGroupings(data.groupings || { themes: [], matchTypes: [], categories: [] });
        setSearchIntent(data.intent);
        setActiveFilters({ theme: null, matchType: null, category: null }); // Reset filters on new search
        setIsSearchMode(true);
        
        // Also set stashItems for backward compatibility with existing UI
        setStashItems(data.results || []);
        setBrokenImages(new Set());
        setImageCacheBuster(Date.now());
      } else {
        // Fallback to regular search on error
        console.warn('Smart search returned non-OK status:', response.status);
        await fetchStashItems();
      }
    } catch (error) {
      console.error('Smart search failed:', error);
      await fetchStashItems();
    } finally {
      setSmartSearching(false);
      setLoading(false);
    }
  };
  
  const clearSearchMode = () => {
    setIsSearchMode(false);
    setBestMatches([]);
    setAllSearchResults([]);
    setSearchGroupings({ themes: [], matchTypes: [], categories: [] });
    setActiveFilters({ theme: null, matchType: null, category: null });
    setSearchQuery('');
    fetchStashItems();
  };
  
  // Filter results based on active filters
  const getFilteredResults = () => {
    if (!isSearchMode) return stashItems;
    
    let results = allSearchResults;
    
    // Apply theme filter - use backend-provided detected_themes
    if (activeFilters.theme) {
      results = results.filter(item => 
        item.detected_themes && item.detected_themes.includes(activeFilters.theme)
      );
    }
    
    // Apply match type filter
    if (activeFilters.matchType) {
      results = results.filter(item => 
        item.match_reasons && item.match_reasons.includes(activeFilters.matchType)
      );
    }
    
    // Apply category filter
    if (activeFilters.category) {
      results = results.filter(item => item.category === activeFilters.category);
    }
    
    return results;
  };
  
  const filteredResults = getFilteredResults();

  const fetchStashItems = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortBy === 'created_at' ? 'desc' : 'asc');

      const response = await fetch(`${BACKEND_URL}/api/stash?${params}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const items = await response.clone().json();
        setStashItems(items);
        // Clear broken images and update cache buster on successful fetch
        setBrokenImages(new Set());
        setImageCacheBuster(Date.now());
      }
    } catch (error) {
      console.error('Failed to fetch stash items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/stash/${itemId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        fetchStashItems();
        // Refresh user data to update stash count
        if (refreshUser) await refreshUser();
      }
      else alert('Failed to delete item');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete item');
    }
  };

  const openDeleteConfirm = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      handleDeleteItem(itemToDelete.item_id);
      setItemToDelete(null);
    }
  };

  // Workflow selection handlers
  const handleSelectManual = () => {
    setShowWorkflowSelector(false);
    setShowAddModal(true);
  };

  const handleSelectURL = () => {
    setShowWorkflowSelector(false);
    setShowURLFlow(true);
  };

  const handleSelectAI = () => {
    setShowWorkflowSelector(false);
    setShowAIFlow(true);
  };

  const handleItemAdded = async () => {
    fetchStashItems();
    // Refresh user data to update stash count
    if (refreshUser) await refreshUser();
  };

  return (
    <div className="min-h-screen bg-cream pb-20 lg:pb-0">
      <Navigation />

      {/* Main Content - reduced top padding on mobile */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-3 pb-6 sm:py-6">
        {/* Consumption Bar - hidden for unlimited users */}
        {!isUnlimited && (
        <ConsumptionBar 
          currentCount={currentCount}
          limit={currentLimit}
          onUpgradeClick={() => navigate('/billing#pricing-calculator')}
        />
        )}

        {/* Header - Compact on mobile */}
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <h1 className="text-lg sm:text-2xl font-light text-charcoal">My Stash</h1>
            <span className="text-sm text-gray-soft">({stashItems.length})</span>
            {isUnlimited && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 text-white text-xs font-medium"
                data-testid="stash-unlimited-badge">
                <Crown size={14} /> Unlimited
              </span>
            )}
          </div>
          {/* Desktop button moved to persistent floating button at bottom */}
        </div>

        {/* Search Bar with Smart Search Toggle */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 relative">
            {/* Left icon - sparkles or search */}
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              {smartSearchEnabled ? (
                <Sparkles size={18} className="text-sage" />
              ) : (
                <Search size={18} className="text-gray-light" />
              )}
            </div>
            <input
              type="text"
              placeholder={smartSearchEnabled ? "Type & press Enter: 'birthday sentiments'" : "Search by name, brand, or tags..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  e.preventDefault();
                  if (smartSearchEnabled) {
                    performSmartSearch(searchQuery);
                  } else {
                    fetchStashItems();
                  }
                }
              }}
              className={`w-full pl-10 pr-12 py-2.5 border rounded-lg focus:ring-2 transition-all bg-white text-sm ${
                smartSearchEnabled 
                  ? 'border-sage/50 focus:border-sage focus:ring-sage/20' 
                  : 'border-beige focus:border-sage focus:ring-sage/20'
              }`}
              data-testid="stash-search-input"
            />
            {/* Right side - spinner while searching, or search button when ready */}
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              {smartSearching ? (
                <Loader2 size={18} className="text-sage animate-spin" />
              ) : searchQuery.trim() ? (
                <button
                  onClick={() => performSmartSearch(searchQuery)}
                  className="p-1.5 rounded-md bg-sage text-white hover:bg-sage-dark transition-all"
                  title="Search"
                  data-testid="smart-search-btn"
                >
                  <Search size={14} />
                </button>
              ) : null}
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-lg border transition-all ${
              showFilters || selectedCategory
                ? 'border-sage bg-sage/10 text-sage'
                : 'border-beige bg-white text-gray-soft hover:text-charcoal'
            }`}
            title="Filters"
            data-testid="filter-toggle-btn"
          >
            <SlidersHorizontal size={18} />
          </button>
          {/* Desktop-only view mode and refresh */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => fetchStashItems()}
              className="p-2.5 rounded-lg border border-beige bg-white text-gray-soft hover:text-sage hover:border-sage transition-all"
              title="Refresh"
              data-testid="refresh-stash-btn"
            >
              <RefreshCw size={18} />
            </button>
            <div className="flex items-center bg-beige rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-sage shadow-sm' : 'text-gray-soft hover:text-charcoal'}`}
                title="Grid view"
                data-testid="grid-view-btn"
              >
                <Grid3x3 size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white text-sage shadow-sm' : 'text-gray-soft hover:text-charcoal'}`}
                title="List view"
                data-testid="list-view-btn"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-4 p-2 sm:p-3 bg-white rounded-lg border border-beige">
            <div className="flex-1 min-w-[140px]">
              <CategorySelect
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                includeAll
                className="w-full px-3 py-2 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white text-charcoal text-sm"
                data-testid="stash-category-filter"
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); localStorage.setItem('stash_sort', e.target.value); }}
                className="w-full px-3 py-2 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white text-charcoal text-sm"
                data-testid="stash-sort-select"
              >
                <option value="name">Sort: Name</option>
                <option value="created_at">Sort: Recent</option>
              </select>
            </div>
            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory('')}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-soft hover:text-charcoal"
              >
                <X size={14} /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* Smart Search Results - Grouped UI */}
        {isSearchMode && !loading && (
          <div className="mb-6 space-y-3" data-testid="grouped-search-results">
            {/* Compact Filter Bar */}
            <div className="bg-white rounded-lg border border-beige p-3">
              <div className="flex flex-wrap items-center gap-2">
                {/* Results count and clear */}
                <span className="text-sm text-charcoal mr-2">
                  <span className="font-medium">{allSearchResults.length}</span> results
                </span>
                
                {/* Filter chips - inline and compact */}
                {searchGroupings.themes.length > 0 && (
                  <>
                    <span className="text-xs text-gray-soft">|</span>
                    {searchGroupings.themes.slice(0, 5).map((theme) => (
                      <button
                        key={theme.key}
                        onClick={() => setActiveFilters(prev => ({
                          ...prev,
                          theme: prev.theme === theme.key ? null : theme.key
                        }))}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full transition-all ${
                          activeFilters.theme === theme.key
                            ? 'bg-sage text-white'
                            : 'bg-cream text-charcoal hover:bg-beige'
                        }`}
                        data-testid={`theme-chip-${theme.key}`}
                      >
                        {theme.label} <span className="opacity-70">{theme.count}</span>
                      </button>
                    ))}
                  </>
                )}
                
                {searchGroupings.categories.length > 0 && (
                  <>
                    <span className="text-xs text-gray-soft">|</span>
                    {searchGroupings.categories.slice(0, 4).map((cat) => (
                      <button
                        key={cat.key}
                        onClick={() => setActiveFilters(prev => ({
                          ...prev,
                          category: prev.category === cat.key ? null : cat.key
                        }))}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full transition-all ${
                          activeFilters.category === cat.key
                            ? 'bg-sage text-white'
                            : 'bg-beige/50 text-charcoal hover:bg-beige'
                        }`}
                        data-testid={`category-chip-${cat.key}`}
                      >
                        {cat.label} <span className="opacity-70">{cat.count}</span>
                      </button>
                    ))}
                  </>
                )}
                
                {/* Clear all */}
                {(activeFilters.theme || activeFilters.category) && (
                  <button
                    onClick={() => setActiveFilters({ theme: null, matchType: null, category: null })}
                    className="text-xs text-gray-soft hover:text-charcoal flex items-center gap-1 ml-auto"
                    data-testid="clear-filters-btn"
                  >
                    <X size={12} /> Clear
                  </button>
                )}
                
                {/* Clear search - always visible */}
                <button
                  onClick={clearSearchMode}
                  className="text-xs text-sage hover:text-sage-dark ml-auto"
                  data-testid="clear-search-btn"
                >
                  Clear search
                </button>
              </div>
              
              {/* Active filter summary */}
              {(activeFilters.theme || activeFilters.category) && (
                <div className="mt-2 pt-2 border-t border-beige text-xs text-gray-soft">
                  Showing {filteredResults.length} of {allSearchResults.length} items
                </div>
              )}
            </div>
            
            {/* Best Matches - only show if no filters active and has results */}
            {bestMatches.length > 0 && !activeFilters.theme && !activeFilters.category && (
              <BestMatches 
                items={bestMatches}
                onItemClick={(item) => {
                  setSelectedItem(item);
                  setIsDetailModalOpen(true);
                }}
                cacheBuster={imageCacheBuster}
              />
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage mx-auto mb-4" />
            <p className="text-gray-soft">Loading your stash...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && stashItems.length === 0 && !searchQuery && !isSearchMode && (
          <div className="text-center py-20">
            <Package size={64} className="text-sage mx-auto mb-6 opacity-50" />
            <h2 className="text-2xl font-light text-charcoal mb-2">Your stash is empty</h2>
            <p className="text-gray-soft mb-8 max-w-md mx-auto">
              Start building your collection by adding your craft supplies.
            </p>
            <button
              className="bg-sage text-white px-8 py-3 rounded-lg font-medium hover:bg-sage-dark transition-all"
              onClick={() => setShowWorkflowSelector(true)}
              data-testid="add-first-item-btn"
            >
              Add Your First Item
            </button>
          </div>
        )}

        {/* No Search Results */}
        {!loading && filteredResults.length === 0 && searchQuery && (
          <div className="text-center py-20">
            <Search size={64} className="text-gray-light mx-auto mb-6 opacity-50" />
            <h2 className="text-2xl font-light text-charcoal mb-2">No items found</h2>
            <p className="text-gray-soft mb-4">Try adjusting your search terms or filters</p>
            <button onClick={clearSearchMode} className="text-sage hover:text-sage-dark transition-colors">
              Clear search
            </button>
          </div>
        )}

        {/* Grid View */}
        {!loading && filteredResults.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredResults.map((item) => (
              <StashGridItem
                key={item.item_id}
                item={item}
                isBroken={brokenImages.has(item.item_id)}
                imageCacheBuster={imageCacheBuster}
                isSearchMode={isSearchMode}
                onImageError={handleImageError}
                onFixImage={handleFixImage}
                onAddImage={handleAddImage}
                onItemClick={() => { setSelectedItem(item); setIsDetailModalOpen(true); }}
                onEditItem={handleEditItem}
                onDeleteItem={openDeleteConfirm}
              />
            ))}
          </div>
        )}

        {/* List View */}
        {!loading && filteredResults.length > 0 && viewMode === 'list' && (
          <div className="bg-white rounded-xl border border-beige overflow-hidden shadow-sm">
            {filteredResults.map((item, index) => (
              <StashListItem
                key={item.item_id}
                item={item}
                isBroken={brokenImages.has(item.item_id)}
                imageCacheBuster={imageCacheBuster}
                isSearchMode={isSearchMode}
                isLast={index === filteredResults.length - 1}
                onImageError={handleImageError}
                onFixImage={handleFixImage}
                onAddImage={handleAddImage}
                onItemClick={() => { setSelectedItem(item); setIsDetailModalOpen(true); }}
                onEditItem={handleEditItem}
                onDeleteItem={openDeleteConfirm}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <WorkflowSelector
        isOpen={showWorkflowSelector}
        onClose={() => setShowWorkflowSelector(false)}
        onSelectManual={handleSelectManual}
        onSelectURL={handleSelectURL}
        onSelectAI={handleSelectAI}
      />

      <AddItemModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleItemAdded}
      />

      <URLLookupFlow
        isOpen={showURLFlow}
        onClose={() => setShowURLFlow(false)}
        onSuccess={handleItemAdded}
      />

      <AIAssistedFlow
        isOpen={showAIFlow}
        onClose={() => setShowAIFlow(false)}
        onSuccess={handleItemAdded}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setItemToDelete(null); }}
        onConfirm={confirmDelete}
        title="Delete Item"
        message={`This will permanently remove "${itemToDelete?.name}" from your stash.`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmVariant="danger"
      />

      <ItemDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); }}
        item={selectedItem}
        onEdit={() => { 
          setIsDetailModalOpen(false); 
          if (isMobile && selectedItem) {
            navigate(`/stash/edit/${selectedItem.item_id}`);
          } else {
            setIsEditModalOpen(true); 
          }
        }}
        onFixImage={() => { 
          setIsDetailModalOpen(false); 
          if (isMobile && selectedItem) {
            navigate(`/stash/edit/${selectedItem.item_id}?changeImage=true`);
          } else {
            setAutoOpenChangeImage(true); 
            setIsEditModalOpen(true); 
          }
        }}
      />

      {/* Edit Modal - only used on desktop */}
      {!isMobile && (
        <EditItemModal
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setSelectedItem(null); setAutoOpenChangeImage(false); }}
          onSuccess={() => { fetchStashItems(); setSelectedItem(null); setAutoOpenChangeImage(false); }}
          item={selectedItem}
          autoOpenChangeImage={autoOpenChangeImage}
        />
      )}

      {/* Mobile FAB */}
      <div className="sm:hidden">
        <FloatingActionButton
          onClick={() => isAtLimit ? navigate('/billing#pricing-calculator') : setShowWorkflowSelector(true)}
          label={isAtLimit ? 'Limit Reached' : 'Add to Stash'}
          disabled={isAtLimit}
          testId="add-to-stash-fab"
        />
      </div>

      {/* Desktop Persistent Button */}
      <div className="hidden sm:block fixed bottom-8 right-8 z-40">
        <button
          onClick={() => isAtLimit ? navigate('/billing#pricing-calculator') : setShowWorkflowSelector(true)}
          disabled={isAtLimit}
          className={`flex items-center gap-2 px-5 py-3 rounded-full shadow-lg font-medium transition-all ${
            isAtLimit
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-sage text-white hover:bg-sage-dark'
          }`}
          data-testid="add-to-stash-btn-desktop"
        >
          <Plus size={20} />
          {isAtLimit ? 'Limit Reached' : 'Add to Stash'}
        </button>
      </div>
    </div>
  );
};

export default StashInventory;
